import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { useTable, useSortBy, useGlobalFilter, usePagination } from 'react-table';
import { useHistory, useParams } from 'react-router-dom';
import { Spinner, Button, Alert } from 'react-bootstrap';
import axios from '../../../../services/AxiosInstance';
import { getApiUrl } from '../../../../utils/ApiUtils';
import { NoData } from '../../../components/SharedComponents/NoData';
import PageTitle from '../../../layouts/PageTitle';
import { GlobalFilter } from './GlobalFilter';

// Lucide React Icons
import {
  Plus,
  RefreshCw,
  Loader2,
  FileText,
  TrendingUp,
  CheckCircle,
  Clock,
  AlertCircle
} from 'lucide-react';

export const QuoteList = () => {
  const { companyId } = useParams();
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [pageSize, setPageSize] = useState(10);
  const [statistics, setStatistics] = useState(null);
  const [hideExpired, setHideExpired] = useState(false);

  const history = useHistory();

  const fetchQuoteData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const getQuotesApiUrl = getApiUrl(`${companyId}/quotes`);
      const result = await axios.get(getQuotesApiUrl, { withCredentials: true });
      setQuotes(result.data.data);
    } catch (err) {
      setError('Det gick inte att hämta offerterna. Försök igen senare.');
      console.error('Error fetching quotes:', err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [companyId]);

  const fetchStatistics = useCallback(async () => {
    try {
      const statsApiUrl = getApiUrl(`${companyId}/quotes/statistics`);
      const result = await axios.get(statsApiUrl, { withCredentials: true });
      setStatistics(result.data.data);
    } catch (err) {
      console.error('Error fetching quote statistics:', err);
      // Don't set error state for statistics, just log it
    }
  }, [companyId]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchQuoteData();
    fetchStatistics();
  };

  useEffect(() => {
    fetchQuoteData();
    fetchStatistics();
  }, [fetchQuoteData, fetchStatistics]);

  const handleRowClick = (quoteId) => {
    const quote = quotes.find(quote => quote.id === quoteId);
    if (quote) {
      if (quote.status === 'Draft') {
        history.push(`/${companyId}/edit-quote/${quoteId}`);
      } else {
        history.push(`/${companyId}/view-quote/${quoteId}`);
      }
    }
  };

  // Filter quotes based on hideExpired setting
  const filteredQuotes = useMemo(() => {
    if (!hideExpired) {
      return quotes;
    }
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    return quotes.filter(quote => {
      // Keep drafts, accepted, rejected, and cancelled quotes
      if (quote.status !== 'Sent') {
        return true;
      }
      // For Sent quotes, check if they're expired
      if (quote.valid_until) {
        const validUntil = new Date(quote.valid_until);
        validUntil.setHours(0, 0, 0, 0);
        return validUntil >= today;
      }
      // If no valid_until date, keep it
      return true;
    });
  }, [quotes, hideExpired]);

  const COLUMNS = useMemo(() => [
    {
      Header: 'Offertnummer',
      accessor: 'friendly_id',
      Cell: ({ value, row }) => (
        <div className="d-flex align-items-center">
          <div
            className="me-3 d-flex align-items-center justify-content-center"
            style={{
              width: '40px',
              height: '40px',
              borderRadius: '8px',
              backgroundColor: '#3b82f615',
              color: '#3b82f6'
            }}
          >
            <FileText size={18} />
          </div>
          <div>
            <div style={{ fontWeight: '600', fontSize: '0.875rem', color: '#3b82f6' }}>
              {value || '-'}
            </div>
            <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
              {row.original.quote_date}
            </div>
          </div>
        </div>
      ),
    },
    {
      Header: 'Kund',
      accessor: 'customer_name',
      Cell: ({ value }) => (
        <span style={{ fontSize: '0.875rem', fontWeight: '500' }}>
          {value || '-'}
        </span>
      ),
    },
    {
      Header: 'Giltigt till',
      accessor: 'valid_until',
      Cell: ({ value }) => (
        <span style={{ fontSize: '0.875rem' }}>
          {value || '-'}
        </span>
      ),
    },
    {
      Header: 'Status',
      accessor: 'status',
      Cell: ({ value }) => {
        const getStatusConfig = (status) => {
          switch (status) {
            case 'Draft':
              return { bg: '#f3f4f6', color: '#374151', text: 'Utkast' };
            case 'Sent':
              return { bg: '#dbeafe', color: '#1e40af', text: 'Skickad' };
            case 'Accepted':
              return { bg: '#d1fae5', color: '#065f46', text: 'Accepterad' };
            case 'Rejected':
              return { bg: '#fee2e2', color: '#991b1b', text: 'Avböjd' };
            case 'Cancelled':
              return { bg: '#f3f4f6', color: '#374151', text: 'Avbruten' };
            default:
              return { bg: '#f3f4f6', color: '#374151', text: value };
          }
        };

        const config = getStatusConfig(value);

        return (
          <span
            style={{
              fontSize: '0.75rem',
              fontWeight: '500',
              padding: '0.25rem 0.75rem',
              borderRadius: '1rem',
              backgroundColor: config.bg,
              color: config.color,
            }}
          >
            {config.text}
          </span>
        );
      },
    },
    {
      Header: 'Belopp exkl. moms',
      accessor: 'subtotal',
      Cell: ({ value, row }) => {
        if (row.original.status === 'Draft') {
          return <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>-</span>;
        }
        return (
          <span style={{ fontSize: '0.875rem', fontWeight: '600', color: '#059669' }}>
            {new Intl.NumberFormat('sv-SE', {
              style: 'currency',
              currency: row.original.currency || 'SEK'
            }).format(value)}
          </span>
        );
      }
    },
    {
      Header: 'Moms',
      accessor: 'tax_amount',
      Cell: ({ value, row }) => {
        if (row.original.status === 'Draft') {
          return <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>-</span>;
        }
        return (
          <span style={{ fontSize: '0.875rem', fontWeight: '500' }}>
            {new Intl.NumberFormat('sv-SE', {
              style: 'currency',
              currency: row.original.currency || 'SEK'
            }).format(value)}
          </span>
        );
      }
    },
    {
      Header: 'Totalt inkl. moms',
      accessor: 'total_price',
      Cell: ({ value, row }) => {
        if (row.original.status === 'Draft') {
          return <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>-</span>;
        }
        return (
          <span style={{ fontSize: '0.875rem', fontWeight: '600', color: '#1f2937' }}>
            {new Intl.NumberFormat('sv-SE', {
              style: 'currency',
              currency: row.original.currency || 'SEK'
            }).format(value)}
          </span>
        );
      }
    }
  ], []);

  const tableInstance = useTable(
    {
      columns: COLUMNS,
      data: filteredQuotes,
      initialState: { pageSize },
    },
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    state,
    page,
    nextPage,
    previousPage,
    canNextPage,
    canPreviousPage,
    pageOptions,
    gotoPage,
    pageCount,
    setPageSize: setTablePageSize,
    setGlobalFilter,
  } = tableInstance;

  const { globalFilter, pageIndex } = state;

  // Enhanced pagination controls
  const PaginationControls = () => (
    <div
      style={{
        backgroundColor: '#fff',
        borderTop: '1px solid #f1f5f9',
        padding: '1rem 1.5rem'
      }}
    >
      <div className="d-flex justify-content-between align-items-center">
        <div className="d-flex align-items-center gap-3">
          <select
            className="form-select form-select-sm"
            style={{
              width: 'auto',
              fontSize: '0.875rem',
              border: '1px solid #e5e7eb',
              borderRadius: '6px'
            }}
            value={pageSize}
            onChange={e => {
              setPageSize(Number(e.target.value));
              setTablePageSize(Number(e.target.value));
            }}
          >
            {[10, 20, 30, 40, 50].map(size => (
              <option key={size} value={size}>{size} per sida</option>
            ))}
          </select>
          <span style={{ fontSize: '0.875rem', color: '#64748b' }}>
            Visar {page.length} av {filteredQuotes.length} offerter
            {hideExpired && filteredQuotes.length !== quotes.length && (
              <span style={{ color: '#6b7280', marginLeft: '0.5rem' }}>
                (filtrerade från {quotes.length})
              </span>
            )}
          </span>
        </div>

        <div className="d-flex align-items-center gap-2">
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => gotoPage(0)}
            disabled={!canPreviousPage}
            style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
          >
            {'<<'}
          </Button>
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => previousPage()}
            disabled={!canPreviousPage}
            style={{ fontSize: '0.75rem' }}
          >
            Föregående
          </Button>
          <span style={{ fontSize: '0.875rem', color: '#374151', fontWeight: '500' }}>
            Sida {pageIndex + 1} av {pageOptions.length}
          </span>
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => nextPage()}
            disabled={!canNextPage}
            style={{ fontSize: '0.75rem' }}
          >
            Nästa
          </Button>
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => gotoPage(pageCount - 1)}
            disabled={!canNextPage}
            style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
          >
            {'>>'}
          </Button>
        </div>
      </div>
    </div>
  );

  if (loading && !refreshing) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Laddar...</span>
        </Spinner>
      </div>
    );
  }

  return (
    <>
      <style>{`
        .table td {
          vertical-align: middle !important;
        }

        .clickable-row {
          cursor: pointer;
        }

        .clickable-row:hover {
          background-color: #f8f9fa;
        }

        .btn-outline-secondary {
          border-color: #6c757d !important;
        }

        .btn-outline-secondary:hover {
          background-color: #6c757d !important;
          border-color: #6c757d !important;
          color: #fff !important;
        }

        /* Add a refresh button animation */
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        .spin {
          animation: spin 1s linear infinite;
        }

        /* Stats Cards */
        .stats-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 1.25rem;
        }

        .stat-card {
          background: white;
          border: none;
          border-radius: 8px;
          padding: 1.5rem;
          text-align: center;
          transition: all 0.2s ease;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
          position: relative;
          overflow: hidden;
        }

        .stat-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 3px;
          background: linear-gradient(90deg, var(--accent-color, #3b82f6), var(--accent-color-light, #60a5fa));
        }

        .stat-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
        }

        .stat-icon {
          width: 48px;
          height: 48px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 1rem;
          font-size: 1.25rem;
        }

        .stat-value {
          font-size: 1.75rem;
          font-weight: 700;
          margin-bottom: 0.5rem;
          letter-spacing: -0.02em;
        }

        .stat-label {
          color: #6b7280;
          font-size: 0.875rem;
          font-weight: 500;
        }

        .stat-sublabel {
          color: #9ca3af;
          font-size: 0.75rem;
          margin-top: 0.25rem;
        }
      `}</style>

      <PageTitle activeMenu="Offerter" motherMenu="Offerter" />
      <div className="col-12">
        {error && (
          <Alert variant="danger" onClose={() => setError(null)} dismissible>
            {error}
          </Alert>
        )}

        {/* Statistics Cards */}
        {statistics && quotes.length > 0 && (
          <div className="stats-grid mb-4">
            {/* Accepted Quote Value */}
            <div className="stat-card" style={{ '--accent-color': '#059669', '--accent-color-light': '#10b981' }}>
              <div className="stat-icon" style={{ background: '#f0fdf4', color: '#059669' }}>
                <TrendingUp size={24} />
              </div>
              <div className="stat-value" style={{ color: '#059669' }}>
                {new Intl.NumberFormat('sv-SE', { style: 'currency', currency: 'SEK' }).format(statistics.accepted_value)}
              </div>
              <div className="stat-label">Godkända offerter</div>
              <div className="stat-sublabel">
                {statistics.accepted_count} st accepterade
              </div>
            </div>

            {/* Acceptance Rate */}
            <div className="stat-card" style={{ '--accent-color': '#3b82f6', '--accent-color-light': '#60a5fa' }}>
              <div className="stat-icon" style={{ background: '#eff6ff', color: '#3b82f6' }}>
                <CheckCircle size={24} />
              </div>
              <div className="stat-value" style={{ color: '#3b82f6' }}>
                {statistics.acceptance_rate.toFixed(1)}%
              </div>
              <div className="stat-label">Acceptansgrad</div>
              <div className="stat-sublabel">
                {statistics.accepted_count} av {statistics.total_sent} skickade
              </div>
            </div>

            {/* Pending Quotes Value */}
            <div className="stat-card" style={{ '--accent-color': '#d97706', '--accent-color-light': '#f59e0b' }}>
              <div className="stat-icon" style={{ background: '#fffbeb', color: '#d97706' }}>
                <Clock size={24} />
              </div>
              <div className="stat-value" style={{ color: '#d97706' }}>
                {new Intl.NumberFormat('sv-SE', { style: 'currency', currency: 'SEK' }).format(statistics.pending_value)}
              </div>
              <div className="stat-label">Väntande offerter</div>
              <div className="stat-sublabel">
                {statistics.sent_count} st skickade
              </div>
            </div>

            {/* Expired/Draft Quotes */}
            <div className="stat-card" style={{ '--accent-color': '#dc2626', '--accent-color-light': '#ef4444' }}>
              <div className="stat-icon" style={{ background: '#fef2f2', color: '#dc2626' }}>
                <AlertCircle size={24} />
              </div>
              <div className="stat-value" style={{ color: '#dc2626' }}>
                {statistics.expired_count}
              </div>
              <div className="stat-label">Utgångna offerter</div>
              <div className="stat-sublabel">
                {statistics.draft_count} st utkast
              </div>
            </div>
          </div>
        )}

        {quotes.length === 0 && !loading ? (
          <>
            <NoData message="Det finns inga offerter att visa. Skapa en för att kunna se dem här." />
            <div className="d-flex justify-content-center p-4">
              <Button variant="primary" onClick={() => history.push(`/${companyId}/create-quote`)}>
                <Plus size={16} className="me-2" />
                Skapa ny offert
              </Button>
            </div>
          </>
        ) : (
          <>
            {/* Enhanced header with actions */}
            <div className="d-flex align-items-center justify-content-end mb-4">
              <Button
                variant="primary"
                onClick={() => history.push(`/${companyId}/create-quote`)}
              >
                <Plus size={16} className="me-2" />
                Ny offert
              </Button>
            </div>

            {refreshing && (
              <div className="text-center mb-3">
                <Loader2 size={16} className="me-1 spin" />
                Uppdaterar offertlista...
              </div>
            )}

            {/* Enhanced table card */}
            <div className="card shadow-sm border-0" style={{ borderRadius: '12px', overflow: 'hidden' }}>
              <div className="card-body p-0">
                {/* Search filter */}
                <div className="p-4 border-bottom" style={{ backgroundColor: '#fafbfc' }}>
                  <div className="d-flex align-items-center justify-content-between gap-3">
                    <div style={{ flex: 1 }}>
                      <GlobalFilter filter={globalFilter} setFilter={setGlobalFilter} />
                    </div>
                    <div className="d-flex align-items-center" style={{ minWidth: '200px' }}>
                      <label 
                        className="d-flex align-items-center" 
                        style={{ 
                          cursor: 'pointer',
                          fontSize: '0.875rem',
                          color: '#374151',
                          userSelect: 'none'
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={hideExpired}
                          onChange={(e) => setHideExpired(e.target.checked)}
                          style={{
                            marginRight: '0.5rem',
                            width: '16px',
                            height: '16px',
                            cursor: 'pointer',
                            accentColor: '#3b82f6'
                          }}
                        />
                        <span>Dölj utgångna offerter</span>
                      </label>
                    </div>
                  </div>
                </div>

                <div className="table-responsive">
                  <table {...getTableProps()} className="table table-hover mb-0">
                    <thead style={{ backgroundColor: '#fff', borderBottom: '2px solid #f1f5f9' }}>
                      {headerGroups.map((headerGroup, headerGroupIndex) => (
                        <tr {...headerGroup.getHeaderGroupProps()} key={`header-group-${headerGroupIndex}`}>
                          {headerGroup.headers.map((column, columnIndex) => (
                            <th
                              {...column.getHeaderProps(column.getSortByToggleProps())}
                              key={`header-${columnIndex}-${column.id}`}
                              className="fw-semibold border-0 py-4 px-4"
                              style={{
                                cursor: column.canSort ? 'pointer' : 'default',
                                userSelect: 'none',
                                fontSize: '0.875rem',
                                letterSpacing: '0.025em',
                                color: '#1e293b',
                                backgroundColor: 'transparent'
                              }}
                            >
                              <div className="d-flex align-items-center justify-content-between">
                                <span>{column.render('Header')}</span>
                                {column.canSort && (
                                  <span className="ms-2">
                                    {column.isSorted ? (
                                      column.isSortedDesc ?
                                        <i className="fa fa-sort-down" style={{ fontSize: '0.75rem', color: '#3b82f6' }} />
                                        :
                                        <i className="fa fa-sort-up" style={{ fontSize: '0.75rem', color: '#3b82f6' }} />
                                    ) : (
                                      <i className="fa fa-sort" style={{ fontSize: '0.75rem', color: '#cbd5e1', opacity: '0.6' }} />
                                    )}
                                  </span>
                                )}
                              </div>
                            </th>
                          ))}
                        </tr>
                      ))}
                    </thead>
                    <tbody {...getTableBodyProps()}>
                      {page.map((row) => {
                        prepareRow(row);
                        return (
                          <tr
                            {...row.getRowProps()}
                            key={row.id}
                            onClick={() => handleRowClick(row.original.id)}
                            style={{
                              cursor: "pointer",
                              transition: 'all 0.2s ease',
                              borderBottom: '1px solid #f1f5f9'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = '#f8fafc';
                              e.currentTarget.style.transform = 'translateY(-1px)';
                              e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.04)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = 'transparent';
                              e.currentTarget.style.transform = 'translateY(0)';
                              e.currentTarget.style.boxShadow = 'none';
                            }}
                          >
                            {row.cells.map(cell => (
                              <td
                                {...cell.getCellProps()}
                                key={cell.column.id}
                                className="py-4 px-4 align-middle border-0"
                                style={{
                                  fontSize: '0.875rem',
                                  color: '#374151'
                                }}
                              >
                                {cell.value !== null && cell.value !== undefined ? cell.render('Cell') : '-'}
                              </td>
                            ))}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                <PaginationControls />
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default QuoteList;